import type { CustomSymbol } from './custom';
import type { PdfPageData, PdfText } from './pdfExtract';
import type { Page, Sym } from './types';

/*
 * Part numbers from the parts list report of an EPLAN PDF ("Ürün listesi",
 * "Artikelstückliste", "Parts list"): rows of device tag, quantity, part
 * number and page / column. Each row is matched to the device drawn at that
 * page and column whose tag is the same (or one ends with the other: the list
 * may show "-2F1.3" where the drawing shows "F1.3"); only main functions and
 * terminals get the part, so contacts do not count twice.
 */

export interface PartRow { tag: string; part: string; page: string; col: number | null }

const TAG_HEAD = /^(cihaz etiketi|betriebsmittelkennzeichen|gerätekennzeichen|device tag|bmk)$/i;
const PART_HEAD = /^(ürün numarası|artikelnummer|teilenummer|part number|article number|order number|bestellnummer)$/i;
const LOC_HEAD = /^(sayfa ?\/ ?sütun|seite ?\/ ?spalte|page ?\/ ?column)$/i;
const LOC = /^\/(\S+)\.(\d+)$/;

/** Rows of every parts list page. */
export function partRows(doc: PdfPageData[]): PartRow[] {
  const out: PartRow[] = [];
  for (const pg of doc) {
    const tagH = pg.texts.find((t) => TAG_HEAD.test(t.s)), partH = pg.texts.find((t) => PART_HEAD.test(t.s)), locH = pg.texts.find((t) => LOC_HEAD.test(t.s));
    if (!tagH || !partH) continue;
    const top = Math.max(tagH.y, partH.y) + 2, right = locH ? locH.x - 3 : Infinity;
    const rows = new Map<number, PdfText[]>();
    for (const t of pg.texts) {
      if (t.y < top || Math.abs(t.angle) > 1) continue;
      const key = [...rows.keys()].find((y) => Math.abs(y - t.y) < 0.8) ?? t.y;
      (rows.get(key) ?? rows.set(key, []).get(key)!).push(t);
    }
    for (const row of rows.values()) {
      const tag = row.find((t) => /^[-=+]/.test(t.s) && t.x < partH.x);
      // the part number is the widest text between the tag / quantity columns and the page column
      const part = row.filter((t) => t !== tag && t.x > tagH.x && t.x < right && !/^\d+$/.test(t.s)).sort((a, b) => b.w - a.w)[0];
      if (!tag || !part) continue;
      const loc = row.map((t) => LOC.exec(t.s.replace(/\s+/g, ''))).find(Boolean);
      out.push({ tag: tag.s, part: part.s, page: loc?.[1] ?? '', col: loc ? Number(loc[2]) : null });
    }
  }
  return out;
}

const norm = (t: string) => t.replace(/^[-=+\s]+/, '').replace(/\s+/g, '').toUpperCase();

/**
 * Writes the part numbers onto the devices. `pages` carries for each converted page the page name of the PDF and
 * the column of a sym (from its original position). Returns the number of rows matched.
 */
export function applyParts(
  rows: PartRow[],
  pages: { page: Page; name: string; column: (s: Sym) => number | null }[],
  symbols: Map<string, CustomSymbol>,
): number {
  const owns = (s: Sym) => !!s.tag && ['main', 'term'].includes(symbols.get(s.type)?.role ?? '');
  const same = (a: string, b: string) => {
    const x = norm(a), y = norm(b);
    return x === y || x.endsWith(y) || y.endsWith(x);
  };
  let n = 0;
  for (const r of rows) {
    const where = r.page ? pages.filter((p) => p.name === r.page) : pages;
    // the column only decides between devices of the same tag on the page (a device's origin may sit in the next one)
    const cands = where.flatMap((p) => p.page.syms.filter((s) => owns(s) && !s.part).map((s) => ({ s, col: p.column(s) })));
    const pick = (ok: (s: Sym) => boolean) => {
      const m = cands.filter((c) => ok(c.s));
      return (m.find((c) => r.col != null && c.col === r.col) ?? m.sort((a, b) => Math.abs((a.col ?? 0) - (r.col ?? 0)) - Math.abs((b.col ?? 0) - (r.col ?? 0)))[0])?.s;
    };
    const hit = pick((s) => norm(s.tag) === norm(r.tag)) ?? pick((s) => same(s.tag, r.tag));
    if (!hit) continue;
    hit.part = r.part.slice(0, 80);
    n++;
  }
  return n;
}
